<template>
  <div
    class="conversations relative max-height-page overflow-y-auto overflow-x-visible">
    <div class="controls">
      <form-input
        v-bind="searchInputConfig"
        v-model="desiredSearchText"></form-input>
    </div>
    <div class="conversations-wrapper">
      <spinner v-if="asyncStatus.asyncDataLoading" size="2"></spinner>
      <div
        v-for="conversation in displayConversations"
        v-if="displayConversations"
        class="conversation-intro padding-l-vertical cursor-pointer relative"
        :class="{
          current: conversation.uuid === conversationUuid,
          'has-unseen-message': !isLastMessageSeen(conversation),
          'has-own-unseen-message': !isLastInboundMessageSeen(conversation),
        }"
        @click="selectConversation(conversation)">
        <div class="flex gap-m">
          <div class="user-avatars relative">
            <chat-user-avatar
              v-for="profile in getConversationUsers(conversation)"
              v-bind="profile">
            </chat-user-avatar>
          </div>
          <div class="content">
            <span
              class="conversation-title block conversation-participants margin-s-bottom"
              :title="getConversationTitle(conversation)">
              {{ getConversationTitle(conversation) }}
            </span>
            <div class="flex-between flex-middle gap-l">
              <p class="last-message-content block margin-remove width-expand">
                {{ getFirstMessage(conversation)?.content }}
              </p>
              <span class="text-xs block c-alt" style="white-space: nowrap">{{
                translateDate(sampleDate, "timeAgo")
              }}</span>
            </div>
            <badge
              :theme="conversation.uuid === conversationUuid ? 'bg' : 'lead'"
              size="s"
              class="new-messages-counter"
              >{{ getUnseenInboundMessageCount(conversation) }}
              {{
                getUnseenInboundMessageCount(conversation) > messageCount - 1
                  ? "+"
                  : ""
              }}
            </badge>
          </div>
        </div>
        <div class="flex last-message-time width-expand flex-end"></div>
      </div>
      <div
        v-if="!asyncStatus.asyncDataLoading && displayConversations.length < 1">
        <h3>
          {{ translateUcFirst("chat.conversation.noConversationsTitle") }}
        </h3>
      </div>
    </div>
  </div>
</template>

<script>
import ChatUserAvatar from "@/client/components/chat/UserAvatar.vue";
import asyncOperations from "@/client/extensions/composition/asyncOperations";
import useChat from "@/client/extensions/composition/useChat";

export default {
  components: { ChatUserAvatar },
  props: {
    conversationUuid: {
      type: [String, Boolean],
      default: false,
    },
    messageCount: {
      type: Number,
      default: 5,
    },
    searchFieldProps: {
      type: Object,
      default: () => {},
    },
  },
  emits: ["conversations:conversation-selected"],
  setup(props) {
    let { asyncOps, asyncOpsReady, asyncStatus, socket } =
      asyncOperations(props);
    let { chat } = useChat();

    return {
      asyncOps,
      asyncOpsReady,
      asyncStatus,
      socket: socket.init(),
      chat,
    };
  },
  data: function () {
    const timestamp = new Date(new Date().getTime() - 24 * 60 * 60 * 1000);
    const userId = this.$store.getters["user/profile"]?.id;
    return {
      currentConversation: 2,
      timestamp: new Date(new Date().getTime() - 24 * 60 * 60 * 1000),
      sampleDate: new Date(timestamp),
      conversations: false,
      textSearchChangeTimeout: "",
      desiredSearchText: "",
      searchText: "",
      searchInputConfig: {
        name: "userName",
        label: false,
        placeholder: "chat.conversation.ui.textFilterPlaceholder",
        icon: "magnify",
        type: "text",
        validation: {
          minLength: {
            min: 3,
          },
        },
        ...this.searchFieldProps,
      },
      asyncData: {
        conversations: {
          target: "chat/conversation/user/" + userId,
          data: {
            messageCount: this.messageCount,
            // this is not a standard filter, it is better this way with our validation
            //   participantName: computed(() => this.searchText),
            filters: {},
          },
          options: {
            dataMutator: (data) => {
              return data.items;
            },
          },
        },
      },
    };
  },
  async mounted() {
    this.socket = await this.socket;
    await this.socket.waitForUUID();
    this.socket.on("newChatConversation", this.newSocketConversationHandler);
  },
  beforeUnmount() {
    try {
      this.socket.off("newChatConversation", this.newSocketConversationHandler);
    } catch (e) {}
  },
  watch: {
    desiredSearchText(newVal) {
      this.searchText = newVal;
    },
  },
  methods: {
    async reloadConversation(conversationOrConversationUuid) {
      let uuid =
        typeof conversationOrConversationUuid === "object"
          ? conversationOrConversationUuid.uuid
          : conversationOrConversationUuid;

      const userId = this.$store.getters["user/profile"]?.id;
      let data = {
        filters: { uuid },
      };
      let updatedConversationResult = await this.asyncOps.asyncCall(
        "chat/conversation/user/" + userId,
        data
      );

      if (
        updatedConversationResult.hasError ||
        !updatedConversationResult?.data?.items?.[0]
      ) {
        return false;
      }
      let updatedConversation = updatedConversationResult.data.items[0];
      let targetIndex = -1;

      for (let i = 0; i < this.conversations.length; i++) {
        let item = this.conversations[i];
        if (item.uuid === updatedConversation.uuid) {
          targetIndex = i;
          break;
        }
      }

      if (targetIndex > -1) {
        this.conversations[targetIndex] = updatedConversation;
      }
    },
    async startConversationByState(recipients) {
      //  this.chat.startConversation(recipients, 'test composition');
    },
    selectConversation(conversation) {
      this.$emit("conversations:conversation-selected", { conversation });
    },
    getFirstMessage(conversation) {
      try {
        return conversation.ChatMessages[0];
      } catch (e) {
        return false;
      }
    },
    getConversationUsers(conversation) {
      let result = [];
      let ourUuid = this.sg("user.profile")?.uuid;

      if (!conversation.ChatMessages || conversation.ChatMessages.length < 1) {
        return result;
      }

      let msg = conversation.ChatMessages[0];

      if (msg.Sender) {
        result.push(msg.Sender);
      }
      if (Array.isArray(msg.Recipients)) {
        result = [...result, ...msg.Recipients];
      }

      result.sort((a, b) => {
        if (a.uuid === ourUuid) {
          return 1;
        }
        if (b.uuid === ourUuid) {
          return -1;
        }
        return 0;
      });
      return result;
    },
    isLastMessageSeen(conversation) {
      let msg = conversation.ChatMessages[0];
      if (!msg) {
        return true;
      }
      let isSeen = false;
      if (!Array.isArray(msg.Recipients)) {
        return false;
      }
      msg.Recipients.forEach((recipient) => {
        if (!recipient) {
          return false;
        }
        if (recipient.readAt) {
          isSeen = true;
          return false;
        }
      });

      return isSeen;
    },
    isLastInboundMessageSeen(conversation) {
      return this.getUnseenInboundMessageCount(conversation) > 0;
    },
    getUnseenInboundMessageCount(conversation) {
      let total = 0;
      let ourUuid = this.sg("user.profile")?.uuid;
      conversation.ChatMessages.forEach((msg) => {
        if (msg.Sender.uuid === ourUuid) {
          return true;
        }

        let isRead = true;
        msg.Recipients.forEach((user) => {
          if (user.uuid === ourUuid && !user.readAt) {
            isRead = false;
            return false;
          }
        });

        if (!isRead) {
          total++;
        }
        // if we read it - false
        // add to count
      });
      return total;
    },
    getConversationTitle(conversation) {
      let users = this.getConversationUsers(conversation);

      return users.map((user) => user.fullName).join(", ");
    },
    newSocketConversationHandler(data) {
      this.refreshAsyncData();
    },
  },
  computed: {
    displayConversations() {
      if (!this.searchText || this.searchText === "") {
        return this.conversations;
      }

      let results = this.conversations.filter((conversation) => {
        let isValid = false;

        conversation.ChatMessages.forEach((msg) => {
          if (
            msg.Sender.fullName
              .toLowerCase()
              .includes(this.searchText.toLowerCase())
          ) {
            isValid = true;
            return false;
          }

          msg.Recipients.forEach((user) => {
            if (
              user.fullName
                .toLowerCase()
                .includes(this.searchText.toLowerCase())
            ) {
              isValid = true;
              return false;
            }
          });
        });
        return isValid;
      });

      return results;
    },
  },
};
</script>

<style scoped lang="scss">
.user-avatars {
  width: 2.5em;
  flex: 0 0 2.5em;
  height: 2.5em;
  position: relative;
  inset-inline-start: -2px;
  margin-inline-end: 1em;

  .user-avatar {
    position: absolute;
    top: 0;
    inset-inline-start: 0;
    z-index: 3;

    &:nth-child(1) {
      inset-inline-start: 0.7em;
      z-index: 2;
    }

    &:nth-child(2) {
      inset-inline-start: 1.4em;
      z-index: 1;
    }

    &:nth-child(n + 4) {
      display: none;
    }
  }
}

.content {
  max-width: calc(100% - 6em);
}

.last-message-content,
.conversation-participants {
  max-width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  font-size: var(--font-s);
}

.conversations {
  min-height: 100%;
  border-radius: var(--border-radius-s);
  display: flex;
  flex-direction: column;
}

.controls {
  border-top-left-radius: var(--border-radius-s);
  border-top-right-radius: var(--border-radius-s);
  padding-inline: var(--margin-m);
  background-color: var(--c-gray-2);
  padding: var(--margin-l);
}

.conversations-wrapper {
  min-height: 100%;
  border-radius: var(--border-radius-s);
  background-color: var(--c-bg-4);
  padding-inline: var(--margin-m);
  padding: var(--margin-l);
  flex: 100%;
}

.conversation-intro {
  position: relative;
  padding-inline: var(--margin-m);
  box-shadow: var(--box-shadow);
  padding: var(--margin-m);
  border-radius: var(--border-radius-s);
  margin-bottom: var(--margin-l);
  transition: background 350ms ease;

  .conversation-title {
    font-weight: 600;
  }

  .last-message-time {
    color: var(--c-gray-5);
  }

  &.current,
  &.active {
    background-color: var(--c-lead-2);
    color: var(--c-bg);

    .last-message-time {
      color: var(--c-bg-4);
    }
  }
}

.new-messages-counter {
  position: absolute;
  inset-inline-end: var(--margin-m);
  top: 50%;
  transform: translateY(-50%);

  aspect-ratio: 1;
  height: 2em;
  white-space: nowrap;

  :deep(.badge-content) {
    direction: ltr;
    text-align: left;
  }
}
</style>
